import type { LocalizedString } from "../i18n";
import type { RiskBoundsConfig } from "./risk-bounds";

/** How the risk-assessment widget renders a model's options (QP-180). */
export type RiskModelDisplayType = "cards" | "dropdown";

/**
 * QL-212 — who a risk-assessment input belongs to: the goal it was captured on
 * ("goal", today's behaviour) or the client, answered once for the whole flow
 * instance ("global"). Scope is a property of the risk model, not of a flow.
 */
export type RiskInputScope = "goal" | "global";

export const RISK_INPUT_SCOPES = ["goal", "global"] as const satisfies readonly RiskInputScope[];

/**
 * QP-180 — one answer option of a config-driven adjustment question.
 * `deltas` maps an INITIAL score value (stringified integer, e.g. "5") to a
 * signed delta (at most one decimal place, QL-8) applied to the score when
 * this answer is chosen at that initial score. A missing key (or 0) means no
 * adjustment at that score.
 */
export type RiskAdjustmentAnswer = {
  id: string;
  labelI18n: LocalizedString;
  deltas: Record<string, number>;
};

/**
 * QP-180 — a config-driven adjustment question on a risk model. Up to 3 per
 * model (enforced server-side). When `enabled`, each answered question
 * contributes its cell delta; the sum of all is the score adjustment.
 */
export type RiskAdjustmentQuestion = {
  id: string;
  enabled: boolean;
  labelI18n: LocalizedString;
  answers: RiskAdjustmentAnswer[];
};

/** Max adjustment questions allowed per risk model (enforced server-side). */
export const MAX_RISK_ADJUSTMENT_QUESTIONS = 3;

/**
 * Max absolute value of a single adjustment delta. Deltas adjust small
 * matrix-derived scores, and the persisted adjustment sum column is
 * numeric(5,1) — bounding each delta keeps the sum far inside that range.
 */
export const MAX_ADJUSTMENT_DELTA_MAGNITUDE = 100;

/**
 * QL-8 — an adjustment delta is valid iff it is a finite number with at most
 * one decimal place (delta × 10 is an integer) and magnitude of at most
 * MAX_ADJUSTMENT_DELTA_MAGNITUDE. Shared by the risk model service
 * (authoritative) and the editor's inline validation (UX mirror).
 */
export const isValidAdjustmentDelta = (delta: number): boolean =>
  typeof delta === "number" &&
  Number.isFinite(delta) &&
  Math.round(delta * 10) === delta * 10 &&
  Math.abs(delta) <= MAX_ADJUSTMENT_DELTA_MAGNITUDE;

/** Max risk levels allowed per risk model (enforced server-side). */
export const MAX_RISK_LEVELS = 5;

export type RiskModel = {
  id: string;
  nameI18n: LocalizedString;
  descriptionI18n: LocalizedString | null;
  /** "dropdown" = legacy radio lists, "cards" = rich selectable cards (QP-180). */
  displayType: RiskModelDisplayType;
  /** Optional risk-score cap applied when the client's capClientField value matches (QP-180). */
  capLevel: number | null;
  /** Client custom-field id whose value (∈ capFieldValues) activates the cap (QP-180). */
  capClientField: string | null;
  /** Explicit field values (exact match) that trigger the cap. Non-empty when capClientField set. */
  capFieldValues: string[] | null;
  /** QP-180 — config-driven adjustment questions (max 3). Empty = no adjustment. */
  adjustmentQuestions: RiskAdjustmentQuestion[];
  /** Per-score ceilings checked per goal by the compliance charts (GAP-028 / QL-493). Null = feature off. */
  riskBounds: RiskBoundsConfig | null;
  /** QL-212 — whether the time horizon is captured per goal or once per flow instance. */
  timeHorizonScope: RiskInputScope;
  /** QL-212 — whether the risk level (risk tolerance) is captured per goal or once per instance. */
  riskLevelScope: RiskInputScope;
  /** QL-212 — whether the whole adjustment-question set is captured per goal or once per instance. */
  adjustmentQuestionsScope: RiskInputScope;
  isActive: boolean;
  tenantId: string;
  createdById: string;
  updatedById: string;
  createdAt: string;
  updatedAt: string;
};

export type RiskTimeHorizon = {
  id: string;
  riskModelId: string;
  labelI18n: LocalizedString;
  /** Card-mode description shown under the label (QP-180). */
  descriptionI18n: LocalizedString | null;
  /** Raw SVG markup rendered as the card icon (QP-180). */
  iconSvg: string | null;
  order: number;
  createdAt: string;
  updatedAt: string;
};

export type RiskLevel = {
  id: string;
  riskModelId: string;
  labelI18n: LocalizedString;
  /** Card-mode description shown under the label (QP-180). */
  descriptionI18n: LocalizedString | null;
  /** Raw SVG markup rendered as the card icon (QP-180). */
  iconSvg: string | null;
  order: number;
  createdAt: string;
  updatedAt: string;
};

export type RiskMatrixScore = {
  id: string;
  riskModelId: string;
  timeHorizonId: string;
  riskLevelId: string;
  score: number;
  createdAt: string;
  updatedAt: string;
};

/**
 * QL-212 — the model's three scopes on their own. Derived from `RiskModel` so
 * the two can never drift; `risk-input-scope` returns this shape normalised.
 */
export type RiskModelScopes = Pick<
  RiskModel,
  "timeHorizonScope" | "riskLevelScope" | "adjustmentQuestionsScope"
>;

export type RiskModelWithDetails = RiskModel & {
  timeHorizons: RiskTimeHorizon[];
  riskLevels: RiskLevel[];
  matrixScores: RiskMatrixScore[];
};

/**
 * `PATCH /internal/risk-models/:id` — the update payload. `nameI18n` is always
 * written; every other field is optional and an omitted field is left
 * unchanged. The three QL-212 scopes follow that same rule.
 */
export type UpdateRiskModelRequest = {
  nameI18n: LocalizedString;
  descriptionI18n?: LocalizedString;
  /** QP-180 — "cards" or "dropdown" option rendering. */
  displayType?: RiskModelDisplayType;
  /** QP-180 — optional risk-score cap; null clears it. */
  capLevel?: number | null;
  capClientField?: string | null;
  /** Explicit field values (exact match) that trigger the cap. Required with capClientField. */
  capFieldValues?: string[] | null;
  /** QP-180 — config-driven adjustment questions (max 3). */
  adjustmentQuestions?: RiskAdjustmentQuestion[];
  /** GAP-028 — goal-level risk bounds (QL-493); null = feature off. */
  riskBounds?: RiskBoundsConfig | null;
} & Partial<RiskModelScopes>;

export type UpdateRiskModelResponse = { success: boolean };

/**
 * `POST /internal/risk-models/:id/clone` — the naming payload (QL-212, D15).
 * The copy takes its whole configuration from the source model; only the name,
 * and optionally the description, come from the caller. The response is the
 * created `RiskModel` row.
 */
export type CloneRiskModelRequest = {
  nameI18n: LocalizedString;
  descriptionI18n?: LocalizedString;
};

/**
 * QL-212 — the four parts the risk-assessment widget is made of. Which of them
 * render in a given container (a flow page, later a PDF section) is decided by
 * the model's three scopes; see `renderedRiskSections` in `risk-input-scope`.
 *
 * "result" is the Initial · Adjustment · Final · Level block. It is not an
 * input: it follows the score, so it renders wherever the score is the page's
 * own conclusion rather than a value borrowed from elsewhere.
 */
export type RiskSection = "timeHorizon" | "riskLevel" | "adjustmentQuestions" | "result";

export const RISK_SECTIONS = [
  "timeHorizon",
  "riskLevel",
  "adjustmentQuestions",
  "result",
] as const satisfies readonly RiskSection[];

/**
 * QL-212 — why a set of effective risk inputs cannot be scored yet. These are
 * "the client has not finished answering" reasons, never configuration errors:
 * a matrix with no cell for an answered pair is a failure, not an incompleteness.
 */
export type RiskInputsIncompleteReason =
  | "no_time_horizon"
  | "no_risk_level"
  | "unanswered_adjustment_questions";

/**
 * QL-212 — the completeness verdict for a goal's effective inputs. Feeds both
 * the apply step (which reports a goal as pending with this reason) and the
 * completion engine.
 */
export type RiskCompletenessVerdict =
  | { scoreable: true }
  | {
      scoreable: false;
      reason: RiskInputsIncompleteReason;
      /** Present only for "unanswered_adjustment_questions" — which ones. */
      unansweredQuestionIds?: string[];
    };

/**
 * QL-353 — stable code of the 409 a risk-model delete answers while
 * something still uses the model, so callers branch on it instead of on
 * wording (precedent: instrument_in_use).
 */
export const RISK_MODEL_IN_USE_CODE = "risk_model_in_use" as const;

/** QL-353 — the 409 body of a risk-model delete that something still blocks. */
export type RiskModelInUseResponse = {
  /** Names the flows and portfolio collections that use the model. */
  error: string;
  code: typeof RISK_MODEL_IN_USE_CODE;
};
